/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import React from "react";
import { User as UserIcon } from "lucide-react";

import IconTile from "@src/components/ui/IconTile";
import {
  Cell,
  FlexCell,
  ListRow,
  Muted,
  Pill,
  Primary,
  Secondary,
  Stack,
} from "@src/components/ui/Lists/ListTable";
import type { ListColumn } from "@src/components/ui/Lists/ListTable";
import type { DropdownAction } from "@src/components/ui/Dropdowns/ActionDropdown";

import type { User } from "@src/@types/User";

export const USER_COLUMNS: ListColumn[] = [
  { key: "name", label: "Name", width: "minmax(220px, 1.6fr)" },
  { key: "email", label: "Email", width: "minmax(200px, 1.4fr)" },
  { key: "project", label: "Primary project", width: "minmax(160px, 1fr)" },
  { key: "status", label: "Status", width: "112px" },
];

export const EnabledPill = ({ enabled }: { enabled?: boolean }) =>
  enabled === false ? <Pill tone="neutral">Disabled</Pill> : <Pill tone="success">Enabled</Pill>;

type Props = {
  item: User;
  onClick: () => void;
  getProjectName: (projectId: string | null | undefined) => string;
  selected?: boolean;
  selectable?: boolean;
  onSelectedChange?: (value: boolean) => void;
  actions?: DropdownAction[];
};
@observer
class UserListItem extends React.Component<Props> {
  render() {
    const { item } = this.props;
    const project = this.props.getProjectName(item.project_id);
    return (
      <ListRow
        columns={USER_COLUMNS}
        selectable={this.props.selectable}
        selected={this.props.selected}
        onSelectedChange={this.props.onSelectedChange}
        onClick={this.props.onClick}
        actions={this.props.actions}
        testId="userListItem"
      >
        <FlexCell gap={12}>
          <IconTile icon={UserIcon} size={28} />
          <Stack>
            <Primary title={item.name}>{item.name}</Primary>
            {item.description ? <Secondary title={item.description}>{item.description}</Secondary> : null}
          </Stack>
        </FlexCell>
        <Cell title={item.email || ""}>{item.email || <Muted>—</Muted>}</Cell>
        <Cell title={project}>{project && project !== "-" ? project : <Muted>—</Muted>}</Cell>
        <div>
          <EnabledPill enabled={item.enabled} />
        </div>
      </ListRow>
    );
  }
}

export default UserListItem;
